/**
 * Gates an autoplaying in-grid banner video on viewport proximity.
 *
 * Lazy (below-the-fold) banners render their video with `preload="none"` and no
 * `autoplay` attribute (see snippets/product-grid-banner.liquid), so the clip never
 * downloads until the shopper scrolls near it. This element starts playback on approach
 * and pauses it on departure. Eager (above-the-fold) banners keep native autoplay and
 * are never wrapped in this element.
 */
class BannerVideo extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    const video = this.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;

    // Muted + inline is what lets a video autoplay without a user gesture.
    video.muted = true;
    video.playsInline = true;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !document.hidden) {
            // preload="none" leaves the element at NETWORK_EMPTY until we ask.
            if (video.networkState === HTMLMediaElement.NETWORK_EMPTY) video.load();
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        }
      },
      // Start a little before the banner is visible so it's already playing on arrival.
      { rootMargin: '300px' }
    );
    this.#observer.observe(video);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }
}

if (!customElements.get('banner-video')) {
  customElements.define('banner-video', BannerVideo);
}
